import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  Modal,
  message,
  Select,
  Space,
  Switch,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Event {
  id: string;
  title: string;
}

interface Question {
  id?: string;
  question: string;
  type: string;
  required: boolean;
  order: number;
  options?: string[];
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: {
    min?: string;
    max?: string;
  };
}

interface FeedbackFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  form: any;
  events: Event[];
}

// A `value` ADAT: a backend kérdéstípus-enumja, ami az adatbázisba íródik és
// amire a kiértékelés kapcsol. Soha nem fordítjuk. A `labelKey` a hozzá tartozó
// felirat, ami a panel nyelvét követi — a kettőt ezért tartjuk külön.
const questionTypes = [
  { value: 'TEXT', labelKey: 'feedbackFormModal.typeText' },
  { value: 'RATING', labelKey: 'feedbackFormModal.typeRating' },
  { value: 'SINGLE_CHOICE', labelKey: 'feedbackFormModal.typeSingleChoice' },
  { value: 'MULTIPLE_CHOICE', labelKey: 'feedbackFormModal.typeMultipleChoice' },
  { value: 'SCALE', labelKey: 'feedbackFormModal.typeScale' },
  { value: 'YES_NO', labelKey: 'feedbackFormModal.typeYesNo' },
  // `as const`: enélkül a `labelKey` sima `string` lenne, a `t()` viszont csak a
  // szótárban létező kulcsokat fogadja el — a szűkítés az, ami a nem létező
  // kulcsot már fordításkor megfogja.
] as const;

const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  form: editingForm,
  events,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [questions, setQuestions] = React.useState<Question[]>([]);

  useEffect(() => {
    if (visible) {
      if (editingForm) {
        // Load existing form data
        form.setFieldsValue({
          title: editingForm.title,
          description: editingForm.description,
          eventId: editingForm.eventId,
          isActive: editingForm.isActive,
          dateRange:
            editingForm.openAt || editingForm.closeAt
              ? [
                  editingForm.openAt ? dayjs(editingForm.openAt) : null,
                  editingForm.closeAt ? dayjs(editingForm.closeAt) : null,
                ]
              : undefined,
        });

        // Load questions if editing
        if (editingForm.id) {
          fetchQuestions(editingForm.id);
        }
      } else {
        form.resetFields();
        setQuestions([]);
      }
    }
  }, [visible, editingForm]);

  const fetchQuestions = async (formId: string) => {
    try {
      const response = await api.get(`/feedback/forms/${formId}`);
      setQuestions(response.data.questions || []);
    } catch (error) {
      message.error(t('feedbackFormModal.questionsLoadFailed'));
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const dateRange = values.dateRange;
      const payload = {
        title: values.title,
        description: values.description,
        eventId: values.eventId,
        isActive: values.isActive,
        openAt: dateRange?.[0]?.toISOString(),
        closeAt: dateRange?.[1]?.toISOString(),
      };

      if (editingForm) {
        await api.patch(`/feedback/forms/${editingForm.id}`, payload);
        message.success(t('feedbackFormModal.updateSuccess'));
      } else {
        // Create form with questions
        const response = await api.post('/feedback/forms/with-questions', {
          form: payload,
          questions: questions.map((q, index) => ({
            ...q,
            order: index,
          })),
        });
        message.success(t('feedbackFormModal.createSuccess'));
      }

      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('feedbackFormModal.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      question: '',
      type: 'TEXT',
      required: false,
      order: questions.length,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setQuestions(updatedQuestions);
  };

  const deleteQuestion = async (index: number) => {
    const question = questions[index];

    if (question.id) {
      // Delete from server if it exists
      try {
        await api.delete(`/feedback/questions/${question.id}`);
        message.success(t('feedbackFormModal.questionDeleted'));
      } catch (error) {
        message.error(t('feedbackFormModal.questionDeleteFailed'));
        return;
      }
    }

    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  const saveQuestion = async (question: Question, formId: string) => {
    try {
      if (question.id) {
        await api.patch(`/feedback/questions/${question.id}`, question);
      } else {
        await api.post(`/feedback/forms/${formId}/questions`, question);
      }
      message.success(t('feedbackFormModal.questionSaved'));
      fetchQuestions(formId);
    } catch (error) {
      message.error(t('feedbackFormModal.questionSaveFailed'));
    }
  };

  const renderQuestionConfig = (question: Question, index: number) => {
    switch (question.type) {
      case 'SINGLE_CHOICE':
      case 'MULTIPLE_CHOICE':
        return (
          <div style={{ marginTop: 10 }}>
            <Input.TextArea
              placeholder={t('feedbackFormModal.optionsPlaceholder')}
              value={question.options?.join('\n') || ''}
              onChange={(e) =>
                updateQuestion(index, 'options', e.target.value.split('\n').filter(Boolean))
              }
              rows={3}
            />
          </div>
        );

      case 'SCALE':
        return (
          <Space style={{ marginTop: 10 }}>
            <Input
              type="number"
              placeholder={t('feedbackFormModal.scaleMin')}
              value={question.scaleMin}
              onChange={(e) => updateQuestion(index, 'scaleMin', parseInt(e.target.value))}
              style={{ width: 80 }}
            />
            <span>{t('feedbackFormModal.scaleTo')}</span>
            <Input
              type="number"
              placeholder={t('feedbackFormModal.scaleMax')}
              value={question.scaleMax}
              onChange={(e) => updateQuestion(index, 'scaleMax', parseInt(e.target.value))}
              style={{ width: 80 }}
            />
            <Input
              placeholder={t('feedbackFormModal.scaleMinLabel')}
              value={question.scaleLabels?.min}
              onChange={(e) =>
                updateQuestion(index, 'scaleLabels', {
                  ...question.scaleLabels,
                  min: e.target.value,
                })
              }
              style={{ width: 120 }}
            />
            <Input
              placeholder={t('feedbackFormModal.scaleMaxLabel')}
              value={question.scaleLabels?.max}
              onChange={(e) =>
                updateQuestion(index, 'scaleLabels', {
                  ...question.scaleLabels,
                  max: e.target.value,
                })
              }
              style={{ width: 120 }}
            />
          </Space>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={editingForm ? t('feedbackFormModal.editTitle') : t('feedbackFormModal.createTitle')}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={800}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="title"
          label={t('feedbackFormModal.formTitle')}
          rules={[{ required: true, message: t('feedbackFormModal.formTitleRequired') }]}
        >
          <Input placeholder={t('feedbackFormModal.formTitlePlaceholder')} />
        </Form.Item>

        <Form.Item name="description" label={t('feedbackFormModal.description')}>
          <TextArea rows={3} placeholder={t('feedbackFormModal.descriptionPlaceholder')} />
        </Form.Item>

        <Form.Item
          name="eventId"
          label={t('feedbackFormModal.event')}
          rules={[{ required: true, message: t('feedbackFormModal.eventRequired') }]}
        >
          <Select placeholder={t('feedbackFormModal.eventPlaceholder')} disabled={!!editingForm}>
            {events.map((event) => (
              <Option key={event.id} value={event.id}>
                {event.title}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="dateRange" label={t('feedbackFormModal.activePeriod')}>
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={[t('feedbackFormModal.openAt'), t('feedbackFormModal.closeAt')]}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="isActive" label={t('feedbackFormModal.active')} valuePropName="checked">
          <Switch />
        </Form.Item>

        {editingForm && (
          <>
            <Divider>{t('feedbackFormModal.questions')}</Divider>

            <List
              dataSource={questions}
              renderItem={(question, index) => (
                <Card size="small" style={{ marginBottom: 10 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <DragOutlined style={{ cursor: 'move' }} />
                      <Input
                        placeholder={t('feedbackFormModal.questionTextPlaceholder')}
                        value={question.question}
                        onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                        style={{ width: 300 }}
                      />
                      <Select
                        value={question.type}
                        onChange={(value) => updateQuestion(index, 'type', value)}
                        style={{ width: 150 }}
                      >
                        {questionTypes.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {t(type.labelKey)}
                          </Option>
                        ))}
                      </Select>
                      <Switch
                        checkedChildren={t('feedbackFormModal.required')}
                        unCheckedChildren={t('feedbackFormModal.optional')}
                        checked={question.required}
                        onChange={(checked) => updateQuestion(index, 'required', checked)}
                      />
                      {question.id && (
                        <Button size="small" onClick={() => saveQuestion(question, editingForm.id)}>
                          {t('feedbackFormModal.saveQuestion')}
                        </Button>
                      )}
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteQuestion(index)}
                      />
                    </Space>
                    {renderQuestionConfig(question, index)}
                  </Space>
                </Card>
              )}
            />

            <Button type="dashed" onClick={addQuestion} block icon={<PlusOutlined />}>
              {t('feedbackFormModal.addQuestion')}
            </Button>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default FeedbackFormModal;
